import React, { useState, useEffect } from 'react';
import {
  Button,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from '@mui/material';
import {
  Login,
  Logout,
  Person,
  OpenInNew,
} from '@mui/icons-material';
import { signInWithPopup, signOut } from 'firebase/auth';
import { auth, googleProvider, isFirebaseConfigured } from '../firebase/config';

// Detect browsers where Google OAuth is blocked (403: disallowed_useragent).
// Google blocks sign-in in WKWebView (iOS in-app browsers, standalone PWAs)
// and Android WebView contexts.
const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isAndroid = /Android/i.test(navigator.userAgent);
// iOS in-app browser: iPhone/iPad in UA but no Safari / CriOS / FxiOS token
const isIOSInAppBrowser = isIOS && !/Safari|CriOS|FxiOS/i.test(navigator.userAgent);
// iOS PWA added to home screen (runs as standalone WKWebView)
const isIOSStandalonePWA = isIOS && window.navigator.standalone === true;
// Android WebView has the 'wv' marker in its UA string
const isAndroidWebView = isAndroid && /wv/i.test(navigator.userAgent);
// Combined: contexts where Google OAuth will return disallowed_useragent
const isRestrictedBrowser = isIOSInAppBrowser || isIOSStandalonePWA || isAndroidWebView;

function AuthButton({ user }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showSafariDialog, setShowSafariDialog] = useState(false);
  const [copied, setCopied] = useState(false);
  const open = Boolean(anchorEl);

  // Clean up any stale redirect state left by previous signInWithRedirect calls
  useEffect(() => {
    if (!auth) return;
    // Importing dynamically to avoid bundle bloat — only needed once on mount
    import('firebase/auth').then(({ getRedirectResult }) => {
      getRedirectResult(auth).catch((error) => {
        if (error.code === 'auth/unauthorized-domain') {
          alert('⚠️ Domain Not Authorized\n\nThis domain needs to be added to Firebase Console.');
        } else if (error.code && error.code !== 'auth/no-auth-event') {
          console.error('Sign-in redirect error:', error);
        }
      });
    });
  }, []);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSignIn = async () => {
    if (!isFirebaseConfigured) {
      window.confirm(
        '🔧 Firebase Not Configured\n\n' +
        'Google Sign-In requires Firebase setup. You have two options:\n\n' +
        '1. Continue WITHOUT Sign-In (Recommended for quick start)\n' +
        '   - App works fully\n' +
        '   - Store API key locally\n' +
        '   - Notes saved in browser\n\n' +
        '2. Set up Firebase (For cloud sync)\n' +
        '   - Enable Google Sign-In\n' +
        '   - Sync across devices\n\n' +
        'Click OK to continue without sign-in.'
      );
      return;
    }

    if (!auth || !googleProvider) {
      alert('Firebase initialization failed. Please check your Firebase configuration.');
      return;
    }

    // In-app browsers (WhatsApp, Instagram, iOS PWA, Android WebView) block
    // Google OAuth with Error 403: disallowed_useragent.
    // Show a dialog guiding the user to open in Safari/Chrome instead.
    if (isRestrictedBrowser) {
      setShowSafariDialog(true);
      return;
    }

    try {
      googleProvider.setCustomParameters({ prompt: 'select_account' });
      // signInWithPopup works in regular Safari (opens a new tab with proper UA)
      // and in all desktop browsers.
      await signInWithPopup(auth, googleProvider);
      console.log('✅ Firebase authentication successful');
      handleClose();
    } catch (error) {
      console.error('Sign-in error:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        console.log('User closed the sign-in popup');
      } else if (error.code === 'auth/popup-blocked') {
        alert('🚫 Popup Blocked\n\nYour browser blocked the sign-in popup.\n\nTo fix:\n1. Allow popups for this site\n2. Try again');
      } else if (error.code === 'auth/unauthorized-domain') {
        alert('⚠️ Domain Not Authorized\n\nYour current domain needs to be authorized in Firebase Console.');
      } else {
        alert('Failed to sign in: ' + error.message);
      }
    }
  };

  const handleSignOut = async () => {
    try {
      localStorage.removeItem('google_access_token');
      await signOut(auth);
      handleClose();
    } catch (error) {
      console.error('Sign-out error:', error);
      alert('Failed to sign out: ' + error.message);
    }
  };

  const handleCopyLink = () => {
    const url = 'https://www.ekamanam.com';
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Fallback for browsers that block clipboard
      alert('Copy this link and paste it in Safari:\n\nhttps://www.ekamanam.com');
    });
  };

  if (!user) {
    return (
      <>
        <Button
          variant="outlined"
          startIcon={<Login />}
          onClick={handleSignIn}
        >
          Sign In
        </Button>

        {/* Shown when user is in an in-app browser / iOS PWA */}
        <Dialog
          open={showSafariDialog}
          onClose={() => setShowSafariDialog(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>Open in Safari to Sign In</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Google sign-in is blocked in in-app browsers and installed apps.
              Please open Ekamanam directly in <strong>Safari</strong> to sign in.
            </Typography>
            <Box
              sx={{
                mt: 2,
                px: 1.5,
                py: 1,
                bgcolor: 'grey.100',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                wordBreak: 'break-all',
                color: 'text.primary',
              }}
            >
              https://www.ekamanam.com
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
            <Button onClick={() => setShowSafariDialog(false)} size="small">
              Cancel
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<OpenInNew />}
              onClick={handleCopyLink}
            >
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<OpenInNew />}
              onClick={() => {
                window.open('https://www.ekamanam.com', '_blank');
                setShowSafariDialog(false);
              }}
            >
              Open in Safari
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Button
        onClick={handleClick}
        startIcon={
          <Avatar
            src={user.photoURL || undefined}
            alt={user.displayName}
            sx={{ width: 32, height: 32 }}
            slotProps={{
              img: {
                referrerPolicy: 'no-referrer',
                onError: () => console.warn('Avatar image failed to load'),
              },
            }}
          >
            {!user.photoURL && (user.displayName?.charAt(0) || user.email?.charAt(0) || 'U')}
          </Avatar>
        }
      >
        {user.displayName?.split(' ')[0] || 'User'}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
      >
        <MenuItem disabled>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">{user.email}</Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleSignOut}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Sign Out
        </MenuItem>
      </Menu>
    </>
  );
}

export default AuthButton;
